import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Star } from "lucide-react";
import { api } from "@/lib/api";

const inp = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors";

export default function ContentView() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/content");
      setData({ hero_badge: "", hero_subtitle: "", pricing_note: "", marquee_items: [], testimonials: [], ...r.data });
    } catch (e) { toast.error("Inhalte konnten nicht geladen werden."); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>;

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  const setItem = (i, v) => setData((d) => ({ ...d, marquee_items: d.marquee_items.map((x, idx) => (idx === i ? v : x)) }));
  const addItem = () => setData((d) => ({ ...d, marquee_items: [...d.marquee_items, ""] }));
  const delItem = (i) => setData((d) => ({ ...d, marquee_items: d.marquee_items.filter((_, idx) => idx !== i) }));
  const moveItem = (i, dir) => setData((d) => {
    const arr = [...d.marquee_items];
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...d, marquee_items: arr };
  });

  const setTesti = (i, field, v) => setData((d) => ({ ...d, testimonials: d.testimonials.map((x, idx) => (idx === i ? { ...x, [field]: v } : x)) }));
  const addTesti = () => setData((d) => ({ ...d, testimonials: [...d.testimonials, { name: "", device: "", text: "", rating: 5 }] }));
  const delTesti = (i) => setData((d) => ({ ...d, testimonials: d.testimonials.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", {
        hero_badge: data.hero_badge,
        hero_subtitle: data.hero_subtitle,
        pricing_note: data.pricing_note,
        marquee_items: data.marquee_items.map((x) => x.trim()).filter(Boolean),
        testimonials: data.testimonials
          .map((t) => ({ name: t.name.trim(), device: t.device.trim(), text: t.text.trim(), rating: t.rating || 5 }))
          .filter((t) => t.name && t.text),
      });
      toast.success("Inhalte gespeichert. Die Website ist aktualisiert.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="content-view">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">Inhalte</h1>
        <button onClick={save} disabled={saving} data-testid="save-content" className="inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-6 py-3 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
          <Save size={16} /> {saving ? "Speichern…" : "Speichern"}
        </button>
      </div>

      <div className="space-y-6 max-w-3xl">
        <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7">
          <h2 className="font-display text-2xl mb-4">Laufschrift (Aktionen & Hinweise)</h2>
          <div className="space-y-2">
            {data.marquee_items.map((it, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`marquee-item-${i}`}>
                <div className="flex flex-col">
                  <button onClick={() => moveItem(i, "up")} className="text-[#666] hover:text-white"><ArrowUp size={13} /></button>
                  <button onClick={() => moveItem(i, "down")} className="text-[#666] hover:text-white"><ArrowDown size={13} /></button>
                </div>
                <input data-testid={`marquee-input-${i}`} value={it} onChange={(e) => setItem(i, e.target.value)} className={`${inp} flex-1`} placeholder="z. B. Sommer-Aktion: -10% auf Displays" />
                <button onClick={() => delItem(i)} className="w-9 h-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
              </div>
            ))}
            <button onClick={addItem} data-testid="add-marquee" className="inline-flex items-center gap-2 text-sm text-[#aaa] hover:text-white transition-colors mt-1"><Plus size={15} /> Eintrag hinzufügen</button>
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7">
          <h2 className="font-display text-2xl mb-1">Kundenstimmen</h2>
          <p className="text-sm text-[#888] mb-4">Name, Gerät und Text frei bearbeiten – so wirken sie laufend aktuell.</p>
          <div className="space-y-4">
            {data.testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-edit-${i}`} className="rounded-2xl border border-white/10 p-4 space-y-2">
                <div className="flex gap-2">
                  <input data-testid={`testimonial-name-${i}`} value={t.name} onChange={(e) => setTesti(i, "name", e.target.value)} placeholder="Name, z. B. Laura M." className={`${inp} flex-1`} />
                  <input data-testid={`testimonial-device-${i}`} value={t.device} onChange={(e) => setTesti(i, "device", e.target.value)} placeholder="Gerät, z. B. iPhone 13" className={`${inp} flex-1`} />
                  <button onClick={() => delTesti(i)} data-testid={`testimonial-delete-${i}`} className="w-11 h-11 shrink-0 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                </div>
                <textarea data-testid={`testimonial-text-${i}`} rows={2} value={t.text} onChange={(e) => setTesti(i, "text", e.target.value)} placeholder="Bewertungstext" className={`${inp} resize-none`} />
                <div className="flex items-center gap-1 pt-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setTesti(i, "rating", n)} data-testid={`testimonial-rating-${i}-${n}`} className="text-amber-400 hover:scale-110 transition-transform">
                      <Star size={18} fill={n <= (t.rating || 5) ? "currentColor" : "none"} />
                    </button>
                  ))}
                  <span className="text-xs text-[#666] ml-1">{t.rating || 5}/5</span>
                </div>
              </div>
            ))}
            <button onClick={addTesti} data-testid="add-testimonial" className="inline-flex items-center gap-2 text-sm text-[#aaa] hover:text-white transition-colors mt-1"><Plus size={15} /> Bewertung hinzufügen</button>
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 space-y-5">
          <h2 className="font-display text-2xl">Startseiten-Texte</h2>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Hero-Badge (kleiner Text oben)</label>
            <input data-testid="hero-badge" value={data.hero_badge} onChange={set("hero_badge")} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Hero-Untertitel</label>
            <textarea data-testid="hero-subtitle" rows={3} value={data.hero_subtitle} onChange={set("hero_subtitle")} className={`${inp} resize-none`} />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Hinweis bei der Preisliste</label>
            <textarea data-testid="pricing-note" rows={3} value={data.pricing_note} onChange={set("pricing_note")} className={`${inp} resize-none`} />
          </div>
        </div>
      </div>
    </div>
  );
}
